// OfferFormView.tsx
import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  createTenderSchema,
  TenderFormData,
} from "../../createTender/schema/tender-schema";
import { Link, useNavigate } from "react-router-dom";
import { z } from "zod";
import { Form } from "@/app/global-components/form";
import AddIcon from "@mui/icons-material/Add";
import { useSelector } from "react-redux";
import { RootState } from "@/store/store";
import { isEmpty } from "lodash";
import { Alert, InputLabel, MenuItem, Select } from "@mui/material";
import SendOfferSubmitView from "./tender-send-submit-view";
import DeleteOfferSubmitView from "../../../app/features/tender/components/view/tender-delete-submit-view";
import CompanyWarningPopup from "@/app/features/CompanyCreate/companycreatePopup";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import Swal from "sweetalert2";
import TenderCloseFormView from "./tender-Closeform-view";
import { routes } from "@/consts";
import useOffer from "@/hooks/useTender";
import useProfile from "@/hooks/use-profile";
import { useDispatch } from "react-redux";
import { getMyCompany } from "@/store/authSlice";
import useRegisterCompany from "@/hooks/use-register-company";
import ItemsTender from "@/pages/createTender/components/items.tender";
import { useShipping } from "@/hooks/use-shipping";
import {
  Box,
  TextField,
  FormControl,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  FormHelperText,
} from "@mui/material";
import { LANG } from "@/types";
import { Search, AlertCircle } from "lucide-react";

interface Product {
  item: string;
  quantity: number;
  unit: string;
  notice: string;
  attachment: File | null;
}
interface CategoryOption {
  value: any;
  label: any;
}
interface TenderEdFormViewProps {
  offer?: Offer | null | undefined;
  disabled?: boolean;
  mutate: () => void;
}
interface Offer {
  title: string;
  value: number;
  endDate: string;
  openingDate: string;
  type: string;
  status: string;
  categories: string[];
  categoriesIds: string[];
  region: string;
  city: string;
  fileName: string;
  fileDescription: string;
  attachmentName: string;
  attachmentDescription: string;
  receiveDocumentsType: string;
  products: Product[];
  Paylater: string;
  attachmentDeliverDays: number | null;
  attachmentId: string | null;
  companyId: string;
  fileId: File | null;
  id: string | null;
  userId: string;
}
const allowedStatuses = [
  "Planning",
  "Opened",
  "Closed",
  "Accepted",
  "Finished",
  "Payment",
  "PaymentConfirmation",
  "Sending",
  "Receiving",
] as const;

const allowedTypes = ["Tiny", "Huge"] as const;
const allowedPaylaterValues = ["Yes", "No"] as const;

function isValidStatus(
  status: string
): status is (typeof allowedStatuses)[number] {
  return allowedStatuses.includes(status as (typeof allowedStatuses)[number]);
}
function isValidType(type: string): type is (typeof allowedTypes)[number] {
  return allowedTypes.includes(type as (typeof allowedTypes)[number]);
}
function isValidPaylater(
  Paylater: string
): Paylater is (typeof allowedPaylaterValues)[number] {
  return allowedPaylaterValues.includes(
    Paylater as (typeof allowedPaylaterValues)[number]
  );
}

const TenderEdFormView: React.FC<TenderEdFormViewProps> = ({
  offer,
  disabled,
  mutate,
}) => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const TenderSchema = createTenderSchema(t as (key: string) => string);
  const navigate = useNavigate();
  const { updateOfferd, getcategories, deleteOffer, isLoading, error } =
    useOffer();

  const [products, setProducts] = useState<Product[]>([
    { item: "", quantity: 1, unit: "", attachment: null, notice: "" },
  ]);
  const [Categoris, setCategoris] = useState<CategoryOption[]>([]);
  const myCompany = useSelector((state: RootState) => state.auth.myCompany);
  const form = useForm<z.infer<typeof TenderSchema>>({
    resolver: zodResolver(TenderSchema),
    defaultValues: {
      id: null,
      title: "",
      value: undefined,
      endDate: "",
      openingDate: "",
      type: "Tiny",
      status: null,
      region: "",
      city: "",
      Paylater: "No",
      categoriesIds: [], // Empty array as default for categories
      fileId: null,
      products: [],
      companyId: myCompany?.id,
    },
  });
  useEffect(() => {
    if (offer) {
      // Ensure all products have the correct attachment type
      const productsWithDefinedAttachments = offer.products.map((product) => ({
        ...product,
        attachment:
          product.attachment &&
          typeof product.attachment === "object" &&
          "url" in product.attachment
            ? null
            : product.attachment,
      }));

      // Validate and assign the status
      const status = isValidStatus(offer.status) ? offer.status : "Planning";
      const type = isValidType(offer.type) ? offer.type : "Tiny";

      const Paylater = isValidPaylater(offer.Paylater) ? offer.Paylater : "No";

      // Format dates for HTML date input (YYYY-MM-DD)
      const formatDateForInput = (dateString: string) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        return date.toISOString().split("T")[0];
      };

      form.reset({
        ...offer,
        status, // Use the validated status
        type,
        endDate: formatDateForInput(offer.endDate),
        openingDate: formatDateForInput(offer.openingDate),
        Paylater,
        products: productsWithDefinedAttachments,
        fileId: offer.fileId,
      });
      setProducts(productsWithDefinedAttachments);
    }
  }, [offer, form]);

  const { getCompanyMine } = useRegisterCompany();
  const [isCompanyAvailable, setIsCompanyAvailable] = useState(false);

  const { getUserProfile } = useProfile();
  const dispatch = useDispatch();
  const { myShipping } = useShipping();
  const [selectedAddress, setSelectedAddress] = useState<string>("");
  useEffect(() => {
    const fetchCompany = async () => {
      await getCompanyMine().catch(() => {
        setIsCompanyAvailable(true);
      });
    };
    fetchCompany();
  }, []);
  useEffect(() => {
    const userProfile = async () => {
      const res = await getUserProfile();
      if (res) {
        form.setValue("companyId", res.data.company.id);
      }

      dispatch(getMyCompany(res.data.company));
    };
    userProfile();
  }, []);
  const [addresses, setAddresses] = useState<any[]>([]);
  useEffect(() => {
    fetchAddresses();
  }, []);

  const fetchAddresses = async () => {
    try {
      const data = await myShipping();
      if (data) {
        setAddresses(data);
        // Set default address if available
        const defaultAddress = data.find((addr: any) => addr.isDefault);
        if (defaultAddress) {
          setSelectedAddress(defaultAddress.id);
        }
      }
    } catch (error) {
      console.error("Error fetching addresses:", error);
    }
  };

  useEffect(() => {
    const fetchCategoris = async () => {
      try {
        const Categories = await getcategories();
        if (Categories) {
          const uniqueCategories = Categories.data.filter(
            (item: { name: any }, index: any, self: any[]) =>
              index === self.findIndex((t) => t.name === item.name) // Only keep unique ids
          );
          const Cates: CategoryOption[] = uniqueCategories.map(
            (item: { id: any; name: any }) => ({
              value: item.id,
              label: item.name,
            })
          );
          setCategoris(Cates);
        } else {
          setCategoris([]);
        }
      } catch (error) {
        console.error("Error fetching Offers:", error);
      }
    };
    fetchCategoris();
  }, []);
  const handleSend = async (values: TenderFormData) => {
    try {
      // Set status to "Opened" before sending
      values.status = "Opened";
      await updateOfferd(values);
      mutate();
      Swal.fire({
        title: t("sendFeedback.success.title"),
        text: t("sendFeedback.success.text"),
        icon: "success",
        confirmButtonText: t("sendFeedback.success.confirmButton"),
      });
    } catch (err) {
      console.error("Error sending offer:", err);
      Swal.fire({
        title: t("sendFeedback.error.title"),
        text: t("sendFeedback.error.text"),
        icon: "error",
        confirmButtonText: t("sendFeedback.error.confirmButton"),
      });
    }
  };
  const handleDelete = async () => {
    if (offer && offer.id) {
      try {
        await deleteOffer(offer.id);
        navigate(routes.tenders);
      } catch (err) {
        console.error("Error deleting offer:", err);
      }
    }
  };

  // Category search for dropdown
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const [categorySearch, setCategorySearch] = useState("");
  const [filteredCategories, setFilteredCategories] = useState<
    CategoryOption[]
  >([]);
  useEffect(() => {
    const filterCategories = () => {
      const filtered = Categoris.filter((category) =>
        category.label.toLowerCase().includes(categorySearch.toLowerCase())
      );
      setFilteredCategories(filtered);
    };
    filterCategories();
  }, [Categoris, categorySearch]);

  return (
    <Form {...form}>
      {isCompanyAvailable && <CompanyWarningPopup open={true} />}
      <form onSubmit={(e) => e.preventDefault()}>
        <Box sx={{ p: 4 }}>
          {error && (
            <Alert
              severity="error"
              icon={<AlertCircle size={20} />}
              sx={{ mb: 3, borderRadius: 2 }}
            >
              {error}
            </Alert>
          )}

          <Grid container spacing={3}>
            {/* Basic Information Section */}
            <Grid item xs={12}>
              <Card sx={{ borderRadius: 2, boxShadow: "none" }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{ mb: 3, fontWeight: 600, color: "#374151" }}
                  >
                    {t("general.purchaseRequest")} {t("general.information")}
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        disabled={disabled}
                        label={
                          t("orders.placeholders.enterRequestTitle") + " *"
                        }
                        {...form.register("title")}
                        error={!!form.formState.errors.title}
                        helperText={form.formState.errors.title?.message}
                        dir={locale === LANG.AR ? "rtl" : "ltr"}
                        inputProps={{
                          dir: locale === LANG.AR ? "rtl" : "ltr",
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            direction: locale === LANG.AR ? "rtl" : "ltr",
                            "&:hover": {
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#644980",
                              },
                            },
                            "&.Mui-focused": {
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#644980",
                                borderWidth: 2,
                              },
                            },
                          },
                          "& .MuiInputLabel-root": {
                            transformOrigin:
                              locale === LANG.AR ? "right" : "left",
                            direction: locale === LANG.AR ? "rtl" : "ltr",
                            ...(locale === LANG.AR
                              ? { right: 30, left: "auto" }
                              : { left: 0, right: "auto" }),
                          },
                          "& legend": {
                            textAlign: locale === LANG.AR ? "end" : "start",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl
                        fullWidth
                        error={!!form.formState.errors.categoriesIds}
                      >
                        <InputLabel
                          dir={locale === LANG.AR ? "rtl" : "ltr"}
                          sx={{
                            transformOrigin:
                              locale === LANG.AR ? "right" : "left",
                            direction: locale === LANG.AR ? "rtl" : "ltr",
                            ...(locale === LANG.AR
                              ? { right: 30, left: "auto" }
                              : { left: 0, right: "auto" }),
                          }}
                        >
                          {t("company.fields.categories")} *
                        </InputLabel>
                        <Select
                          multiple
                          disabled={disabled}
                          open={isCategoryDropdownOpen}
                          onOpen={() => setIsCategoryDropdownOpen(true)}
                          onClose={() => {
                            setIsCategoryDropdownOpen(false);
                            setCategorySearch("");
                          }}
                          value={form.watch("categoriesIds") || []}
                          onChange={(e) =>
                            form.setValue(
                              "categoriesIds",
                              e.target.value as string[]
                            )
                          }
                          label={t("company.fields.categories")}
                          dir={locale === LANG.AR ? "rtl" : "ltr"}
                          MenuProps={{
                            PaperProps: {
                              style: {
                                maxHeight: 300,
                              },
                            },
                            onMouseDown: (e) => {
                              e.stopPropagation();
                            },
                            disableRestoreFocus: true,
                            keepMounted: true,
                          }}
                          renderValue={(selected) => (
                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.5,
                              }}
                            >
                              {(selected as string[]).map((value: string) => {
                                const category = Categoris.find(
                                  (cat) => cat.value === value
                                );
                                return category ? (
                                  <Chip
                                    key={value}
                                    label={category.label}
                                    size="small"
                                    sx={{
                                      backgroundColor: "#e0e7ff",
                                      color: "#3730a3",
                                      fontSize: "0.75rem",
                                    }}
                                  />
                                ) : null;
                              })}
                            </Box>
                          )}
                          sx={{
                            borderRadius: 2,
                            direction: locale === LANG.AR ? "rtl" : "ltr",
                            "& .MuiSelect-select": {
                              paddingRight: "10px !important",
                            },
                            "& .MuiSelect-icon": {
                              left: locale === LANG.EN ? "unset" : 0,
                              marginLeft: 0,
                              right: locale === LANG.EN ? 0 : "unset",
                            },
                            "&:hover": {
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#644980",
                              },
                            },
                            "&.Mui-focused": {
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#644980",
                                borderWidth: 2,
                              },
                            },
                            "& legend": {
                              textAlign: locale === LANG.AR ? "end" : "start",
                            },
                          }}
                        >
                          <Box sx={{ p: 1, borderBottom: "1px solid #e5e7eb" }}>
                            <TextField
                              size="small"
                              placeholder={
                                locale === LANG.EN
                                  ? "Search categories..."
                                  : "البحث في الفئات..."
                              }
                              value={categorySearch}
                              onChange={(e) => {
                                setCategorySearch(e.target.value);
                              }}
                              onMouseDown={(e) => {
                                e.stopPropagation();
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                              InputProps={{
                                startAdornment: (
                                  <Search
                                    size={16}
                                    style={{ marginRight: 8, color: "#6b7280" }}
                                  />
                                ),
                              }}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 1,
                                },
                              }}
                            />
                          </Box>
                          {filteredCategories.map((category) => (
                            <MenuItem
                              key={category.value}
                              value={category.value}
                              onMouseDown={(e) => {
                                e.stopPropagation();
                              }}
                              sx={{
                                borderRadius: 1,
                                margin: "2px 0",
                                "&.Mui-selected": {
                                  backgroundColor: "#644980",
                                  color: "#fff",
                                  fontWeight: "600",
                                  "&:hover": {
                                    backgroundColor: "#644980",
                                  },
                                },
                                "&:hover": {
                                  backgroundColor: "#f3f4f6",
                                },
                              }}
                            >
                              {category.label}
                            </MenuItem>
                          ))}
                          {filteredCategories.length === 0 && (
                            <MenuItem
                              disabled
                              sx={{ textAlign: "center", color: "#6b7280" }}
                            >
                              {locale === LANG.EN
                                ? "No categories found"
                                : "لم يتم العثور على فئات"}
                            </MenuItem>
                          )}
                        </Select>
                        {form.formState.errors.categoriesIds && (
                          <FormHelperText>
                            {form.formState.errors.categoriesIds.message}
                          </FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        disabled={disabled}
                        label={t("tender.value") + " *"}
                        type="number"
                        inputProps={{
                          min: 0,
                          dir: locale === LANG.AR ? "rtl" : "ltr",
                        }}
                        {...form.register("value")}
                        error={!!form.formState.errors.value}
                        helperText={form.formState.errors.value?.message}
                        dir={locale === LANG.AR ? "rtl" : "ltr"}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            direction: locale === LANG.AR ? "rtl" : "ltr",
                            "&:hover": {
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#644980",
                              },
                            },
                            "&.Mui-focused": {
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#644980",
                                borderWidth: 2,
                              },
                            },
                          },
                          "& .MuiInputLabel-root": {
                            transformOrigin:
                              locale === LANG.AR ? "right" : "left",
                            direction: locale === LANG.AR ? "rtl" : "ltr",
                            ...(locale === LANG.AR
                              ? { right: 30, left: "auto" }
                              : { left: 0, right: "auto" }),
                          },
                          "& legend": {
                            textAlign: locale === LANG.AR ? "end" : "start",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        disabled={disabled}
                        type="file"
                        {...form.register("fileId")}
                        dir={locale === LANG.AR ? "rtl" : "ltr"}
                        inputProps={{
                          dir: locale === LANG.AR ? "rtl" : "ltr",
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            direction: locale === LANG.AR ? "rtl" : "ltr",
                            paddingRight: "12px",
                            "&:hover": {
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#644980",
                              },
                            },
                            "&.Mui-focused": {
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#644980",
                                borderWidth: 2,
                              },
                            },
                          },
                          "& .MuiInputLabel-root": {
                            transformOrigin:
                              locale === LANG.AR ? "right" : "left",
                            direction: locale === LANG.AR ? "rtl" : "ltr",
                            ...(locale === LANG.AR
                              ? { right: 30, left: "auto" }
                              : { left: 0, right: "auto" }),
                          },
                          "& legend": {
                            textAlign: locale === LANG.AR ? "end" : "start",
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Items Section */}
            <Grid item xs={12}>
              <Card sx={{ borderRadius: 2, boxShadow: "none" }}>
                <CardContent sx={{ p: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 3,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, color: "#374151" }}
                    >
                      {t("offers.items")}
                    </Typography>
                  </Box>
                  {products.length > 0 && (
                    <ItemsTender
                      disabled={disabled}
                      products={products}
                      form={form}
                      setProducts={setProducts}
                    />
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Dates and Address Section */}
            <Grid item xs={12}>
              <Card sx={{ borderRadius: 2, boxShadow: "none" }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{ mb: 3, fontWeight: 600, color: "#374151" }}
                  >
                    {t("offers.fields.submissionDeadline")} &{" "}
                    {t("checkout.fields.shippingAddress")}
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        disabled={disabled}
                        type="date"
                        {...form.register("endDate")}
                        error={!!form.formState.errors.endDate}
                        helperText={form.formState.errors.endDate?.message}
                        dir={locale === LANG.AR ? "rtl" : "ltr"}
                        inputProps={{
                          min: new Date(
                            new Date().setDate(new Date().getDate() + 1)
                          )
                            .toISOString()
                            .split("T")[0],
                          dir: locale === LANG.AR ? "rtl" : "ltr",
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            direction: locale === LANG.AR ? "rtl" : "ltr",
                            paddingRight: "12px",
                            "&:hover": {
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#644980",
                              },
                            },
                            "&.Mui-focused": {
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#644980",
                                borderWidth: 2,
                              },
                            },
                          },
                          "& .MuiInputLabel-root": {
                            "&[data-shrink='false']": {
                              pr: locale === LANG.AR ? "20px" : "0px",
                            },
                            transformOrigin:
                              locale === LANG.AR ? "right" : "left",
                            direction: locale === LANG.AR ? "rtl" : "ltr",
                            ...(locale === LANG.AR
                              ? { right: 30, left: "auto" }
                              : { left: 0, right: "auto" }),
                          },
                          "& legend": {
                            textAlign: locale === LANG.AR ? "end" : "start",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        disabled={disabled}
                        type="date"
                        {...form.register("openingDate")}
                        error={!!form.formState.errors.openingDate}
                        helperText={form.formState.errors.openingDate?.message}
                        dir={locale === LANG.AR ? "rtl" : "ltr"}
                        inputProps={{
                          min: new Date(
                            new Date().setDate(new Date().getDate() + 2)
                          )
                            .toISOString()
                            .split("T")[0],
                          dir: locale === LANG.AR ? "rtl" : "ltr",
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            direction: locale === LANG.AR ? "rtl" : "ltr",
                            paddingRight: "12px",
                            "&:hover": {
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#644980",
                              },
                            },
                            "&.Mui-focused": {
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#644980",
                                borderWidth: 2,
                              },
                            },
                          },
                          "& .MuiInputLabel-root": {
                            "&[data-shrink='false']": {
                              pr: locale === LANG.AR ? "20px" : "0px",
                            },
                            transformOrigin:
                              locale === LANG.AR ? "right" : "left",
                            direction: locale === LANG.AR ? "rtl" : "ltr",
                            ...(locale === LANG.AR
                              ? { right: 30, left: "auto" }
                              : { left: 0, right: "auto" }),
                          },
                          "& legend": {
                            textAlign: locale === LANG.AR ? "end" : "start",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      {addresses.length > 0 ? (
                        <FormControl fullWidth>
                          <InputLabel
                            dir={locale === LANG.AR ? "rtl" : "ltr"}
                            sx={{
                              transformOrigin:
                                locale === LANG.AR ? "right" : "left",
                              direction: locale === LANG.AR ? "rtl" : "ltr",
                              ...(locale === LANG.AR
                                ? { right: 30, left: "auto" }
                                : { left: 0, right: "auto" }),
                            }}
                          >
                            {t("checkout.fields.shippingAddress")}
                          </InputLabel>
                          <Select
                            disabled={disabled}
                            value={selectedAddress}
                            onChange={(e) => setSelectedAddress(e.target.value)}
                            label={t("checkout.fields.shippingAddress")}
                            dir={locale === LANG.AR ? "rtl" : "ltr"}
                            sx={{
                              borderRadius: 2,
                              direction: locale === LANG.AR ? "rtl" : "ltr",
                              "& .MuiSelect-select": {
                                paddingRight: "10px !important",
                              },
                              "& .MuiSelect-icon": {
                                left: locale === LANG.EN ? "unset" : 0,
                                marginLeft: 0,
                                right: locale === LANG.EN ? 0 : "unset",
                              },
                              "&:hover": {
                                "& .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "#644980",
                                },
                              },
                              "&.Mui-focused": {
                                "& .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "#644980",
                                  borderWidth: 2,
                                },
                              },
                              "& legend": {
                                textAlign: locale === LANG.AR ? "end" : "start",
                              },
                            }}
                          >
                            {addresses.map((address) => (
                              <MenuItem key={address.id} value={address.id}>
                                {address.fullName} - {address.address}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <Box>
                          <Link
                            to={routes.shippingAddress}
                            className="bg-black !mt-5 py-2 rounded-lg px-4 text-white"
                          >
                            <AddIcon />
                            {t("checkout.fields.shippingAddress")}
                          </Link>
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Payment Terms Section */}
            <Grid item xs={12}>
              <Card sx={{ borderRadius: 2, boxShadow: "none" }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{ mb: 3, fontWeight: 600, color: "#374151" }}
                  >
                    {t("offers.fields.paymentTerms")}
                  </Typography>
                  <FormControl fullWidth>
                    <InputLabel
                      dir={locale === LANG.AR ? "rtl" : "ltr"}
                      sx={{
                        transformOrigin: locale === LANG.AR ? "right" : "left",
                        direction: locale === LANG.AR ? "rtl" : "ltr",
                        ...(locale === LANG.AR
                          ? { right: 30, left: "auto" }
                          : { left: 0, right: "auto" }),
                      }}
                    >
                      {t("offers.fields.paymentTerms")}
                    </InputLabel>
                    <Select
                      disabled={disabled}
                      value={form.watch("Paylater") || "No"}
                      onChange={(e) =>
                        form.setValue(
                          "Paylater",
                          e.target.value as "Yes" | "No"
                        )
                      }
                      label={t("offers.fields.paymentTerms")}
                      dir={locale === LANG.AR ? "rtl" : "ltr"}
                      sx={{
                        borderRadius: 2,
                        direction: locale === LANG.AR ? "rtl" : "ltr",
                        "& .MuiSelect-select": {
                          paddingRight: "10px !important",
                        },
                        "& .MuiSelect-icon": {
                          left: locale === LANG.EN ? "unset" : 0,
                          marginLeft: 0,
                          right: locale === LANG.EN ? 0 : "unset",
                        },
                        "&:hover": {
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#644980",
                          },
                        },
                        "&.Mui-focused": {
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#644980",
                            borderWidth: 2,
                          },
                        },
                        "& legend": {
                          textAlign: locale === LANG.AR ? "end" : "start",
                        },
                      }}
                    >
                      <MenuItem value="Yes">{t("payment.deferred")}</MenuItem>
                      <MenuItem value="No">
                        {t("offers.fields.cashPayment")}
                      </MenuItem>
                    </Select>
                  </FormControl>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Action Buttons */}
          {!isEmpty(myCompany) && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mt: 4,
                gap: 2,
              }}
            >
              <DeleteOfferSubmitView
                isLoading={isLoading}
                onClick={handleDelete}
              />
              {offer?.status === "Closed" && (
                <SendOfferSubmitView
                  isLoading={isLoading}
                  onClick={form.handleSubmit(handleSend)}
                />
              )}
              <TenderCloseFormView mutate={mutate} offer={offer} />
            </Box>
          )}
        </Box>
      </form>
    </Form>
  );
};

export default TenderEdFormView;
